import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";

export const STAGE_COLOR_CLASSES = {
  slate: { bg: "bg-slate-100", border: "border-slate-200", text: "text-slate-700", dot: "bg-slate-400", hex: "#94A3B8" },
  sky: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", dot: "bg-sky-500", hex: "#0284C7" },
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", dot: "bg-cyan-500", hex: "#0891B2" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500", hex: "#7C3AED" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", dot: "bg-orange-500", hex: "#EA580C" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500", hex: "#CA8A04" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500", hex: "#16A34A" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500", hex: "#DC2626" },
};

export const STAGE_COLORS = Object.keys(STAGE_COLOR_CLASSES);

const ConfigContext = createContext(null);

export const ConfigProvider = ({ children }) => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [stages, setStages] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);

  const reload = useCallback(async () => {
    if (!user) return;
    const results = await Promise.allSettled([
      api.get("/config/stages"),
      api.get("/config/services"),
      api.get("/settings"),
      api.get("/expense-accounts"),
      api.get("/beneficiaries"),
    ]);
    if (results[0].status === "fulfilled") setStages(results[0].value.data);
    if (results[1].status === "fulfilled") setServices(results[1].value.data);
    if (results[2].status === "fulfilled") setSettings(results[2].value.data);
    if (results[3].status === "fulfilled") setExpenseAccounts(results[3].value.data);
    if (results[4].status === "fulfilled") setBeneficiaries(results[4].value.data);
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  const value = useMemo(() => {
    const activeStages = stages.filter((s) => s.active);
    const byKey = Object.fromEntries(stages.map((s) => [s.key, s]));
    const stageLabel = (key) => {
      const s = byKey[key];
      if (!s) return key;
      return lang === "id" ? s.label_id : s.label_en;
    };
    const stageStyle = (key) => STAGE_COLOR_CLASSES[byKey[key]?.color] || STAGE_COLOR_CLASSES.slate;
    const stagesByWeight = [...activeStages].sort((a, b) => b.probability - a.probability);
    return {
      stages, activeStages, stagesByWeight, services, settings, byKey,
      expenseAccounts, beneficiaries,
      activeAccounts: expenseAccounts.filter((a) => a.active),
      activeBeneficiaries: beneficiaries.filter((b) => b.active),
      stageKeys: activeStages.map((s) => s.key),
      activeServices: services.filter((s) => s.active),
      stageLabel, stageStyle, reload,
      stageRank: (key) => activeStages.findIndex((s) => s.key === key),
    };
  }, [stages, services, settings, expenseAccounts, beneficiaries, lang, reload]);

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => useContext(ConfigContext);
